<template>
  <b-container class="site-info site-edit">
    <b-form @submit="saveSite">
      <b-row>
        <b-col>
          <b-row>
            <b-col class="required-help mb-2">
              Fields marked with a * are required
            </b-col>
          </b-row>
          <b-row class="section-title">
            <h3>{{ SectionTitles.SITE_DETAILS }}</h3>
          </b-row>
          <b-row class="mt-4" :class="site.id ? 'mt-4 mb-2' : ''">
            <b-col>
              <label for="jobNumber">JOB NUMBER<span class="required-label">*</span></label>
              <b-form-input id="jobNumber" v-model="site.jobNumber" required ref="jobNumber"/>
            </b-col>
          </b-row>
          <b-row v-if="!site.id">
            <b-col>
              <label class="w-100 mb-2">
                SITE NAME <span class="required-label">*</span>
                <b-form-input class="mt-2" v-model="site.name" required/>
              </label>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col>
              <label for="address">SITE ADDRESS <span class="required-label">*</span></label>
              <b-form-input id="address" v-model="site.address" required/>
            </b-col>
          </b-row>
          <b-row class="mb-2">
            <b-col>
              <label>SITE CONTACT <span class="required-label">*</span></label>
              <div class="dropdown w-100">
                <vue-typeahead-bootstrap
                  ref="foreperson"
                  :value="foreperson.displayName"
                  :serializer="fp => fp.displayName"
                  @hit="selectForeperson($event)"
                  :data="rawUsers"
                  :minMatchingChars="minChars"
                  :showOnFocus="true"
                  placeholder="Search for a Site Contact..."
                />
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label for="forepersonMobile">SITE CONTACT MOBILE</label>
              <b-form-input id="forepersonMobile" v-model="site.forepersonMobile"/>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label>CONTRACT MANAGER <span class="required-label">*</span></label>
              <div class="dropdown w-100">
                <vue-typeahead-bootstrap
                  ref="owner"
                  class="mb-4"
                  :value="owner.displayName"
                  :serializer="owner => owner.displayName"
                  @hit="selectOwner($event)"
                  :data="rawUsers"
                  :minMatchingChars="minChars"
                  :showOnFocus="true"
                  placeholder="Search for a Contract Manager"
                />
              </div>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="font-weight-bold text-center">Location</b-col>
      </b-row>
      <b-row>
        <b-col class="d-flex justify-content-center">
          <GmapMap
            :center="siteLocation"
            :zoom="15"
            map-type-id="terrain"
            style="width: 500px; height: 300px"
          >
            <GmapMarker
              :position="siteLocation"
              :clickable="true"
              :draggable="true"
              :title="site.name"
              @dragend="updateLocation"
            />
          </GmapMap>
        </b-col>
      </b-row>

      <b-row class="section-title">
        <h3>{{ SectionTitles.CRITICAL_HAZARDS }} <span>Select all that apply</span></h3>
      </b-row>
      <b-row class="section-content-container">
        <b-col class="section-content section-edit" v-for="(risk, index) in applicableCriticalRisks" v-bind:key="'applicableCriticalRisks' + index">
          <b-form-checkbox v-model="risk.applies" :value="true" :unchecked-value="false"><span v-html="risk.risk"></span></b-form-checkbox>
        </b-col>
      </b-row>

      <b-row class="section-title">
        <h3>{{ SectionTitles.ADDITIONAL_HAZARDS }}</h3>
      </b-row>
      <b-row class="section-content-container">
        <b-col class="section-content section-form" v-for="(risk, index) in applicableExtraRisks" v-bind:key="'ff' + index">
          <b-row>
            <b-col>
              <b-form-input v-model="risk.risk" class="mb-3" placeholder="Hazard"/>
              <b-form-textarea v-model="risk.mitigations" rows="3" class="mb-3" placeholder="Risk"/>
              <b-button @click="applicableExtraRisks.splice(index, 1);" variant="danger">Remove</b-button>
            </b-col>
          </b-row>
        </b-col>
        <div class="d-flex flex-column justify-content-center">
          <b-button @click="applicableExtraRisks.push({});" variant="success" class="mt-2">Add Additional Critical Hazard</b-button>
        </div>
      </b-row>
      <b-row class="section-title">
        <h3>{{ SectionTitles.GENERAL_HAZARDS }} <span>Select all that apply</span></h3>
      </b-row>
      <b-row class="section-content-container">
        <b-col class="section-content section-edit" v-for="(risk, index1) in applicableStandardRisks" v-bind:key="'applicableStandardRisks' + index1">
          <b-form-checkbox v-model="risk.applies" :value="true" :unchecked-value="false"><span v-html="risk.risk"></span></b-form-checkbox>
        </b-col>
      </b-row>
      <b-row class="section-title">
        <h3>{{ SectionTitles.WORKSAFE_TASKS }} <span>Select all that apply</span></h3>
      </b-row>
      <b-row class="section-content-container">
        <b-col class="section-content section-edit" v-for="(task, index2) in applicableWorkSafeTasks" v-bind:key="'bb' + index2">
          <b-form-checkbox v-model="task.applies" :value="true" :unchecked-value="false"><span v-html="task.description"/></b-form-checkbox>
        </b-col>
      </b-row>
      <b-row class="section-title">
        <h3>{{ SectionTitles.PPE_REQUIREMENTS }} <span>select all that apply</span></h3>
      </b-row>
      <b-row class="section-content-container">
        <b-col class="section-content section-edit">
        <b-row v-for="(ppeItem, index2) in applicablePPE" v-bind:key="'cc' + index2">
          <b-form-checkbox v-model="ppeItem.applies" :value="true" :unchecked-value="false" :disabled="ppeItem.name === 'Hi-Viz' || ppeItem.name === 'Steel capped boots'"><span v-html="ppeItem.name"/></b-form-checkbox>
        </b-row>
        </b-col>
      </b-row>
      <b-row class="section-title">
        <h3>{{ SectionTitles.SITE_SAFETY }}</h3>
      </b-row>
      <b-row class="section-content-container">

        <b-col class="section-content section-form">
          <b-row>
            <b-col class="mb-n2">First Aider(s) onsite</b-col>
          </b-row>
          <b-row class="form-edit mt-2" v-for="(firstAider, index) in firstAiders" v-bind:key="'fa' + index">
            <b-col>
              <b-form-input v-model="firstAider.name" placeholder="Name"/>
              <b-form-input v-model="firstAider.number" placeholder="Mobile No"/>
              <b-button @click="firstAiders.splice(index, 1);" variant="danger">Remove</b-button>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <div class="mx-n3">
            <b-button @click="firstAiders.push({name: '', number: ''});" variant="success" class="w-100 mb-3">Add First Aider</b-button>
          </div>
        </b-col>
        <b-col class="section-content section-form">
          <b-row>
            <b-col class="mb-n2">Emergency Warden(s)</b-col>
          </b-row>
          <b-row class="mt-2 form-edit" v-for="(emergencyWarden, index) in emergencyWardens" v-bind:key="'ew' + index">
            <b-col>
              <b-form-input v-model="emergencyWarden.name" placeholder="Name"/>
              <b-form-input v-model="emergencyWarden.number" placeholder="Mobile No"/>
              <b-button @click="emergencyWardens.splice(index, 1);" variant="danger">Remove</b-button>
            </b-col>
          </b-row>
        </b-col>
        <b-col>
          <div class="mx-n3">
          <b-button @click="emergencyWardens.push({name: '', number: ''});" variant="success" class="w-100 mb-3">Add Emergency Warden</b-button>
          </div>
        </b-col>
        <b-col class="section-content section-form">
          <b-row>
            <b-col>
              <label for="evacuationPoints">Emergency Evacuation point(s) <span class="required-label">*</span></label>
              <b-form-input id="evacuationPoints" v-model="site.evacuationPoints" required/>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <label for="firstAidKits">Location of First Aid Kits  <span class="required-label">*</span></label>
              <b-form-input id="firstAidKits" v-model="site.firstAidKits" required/>
            </b-col>
          </b-row>
          <b-row v-if="siteLocation">
            <b-col>
              <label>Local Medical Centre <span class="required-label">*</span></label>
              <gmap-autocomplete @place_changed="setMedicalCentre"
                                  :options="{
                                    fields: ['name', 'geometry'],
                                    bounds: google && (new google.maps.Circle({ center: this.siteLocation, radius: 50000 })).getBounds(),
                                    strictBounds: false,
                                    types: ['establishment'],
                                  }"
                                  placeholder="Search for a medical centre..."
                                  class="form-control"></gmap-autocomplete>
            </b-col>
          </b-row>
          <b-row v-if="medicalCentreLocation">
            <b-col>
              <GmapMap
                :center="medicalCentreLocation"
                :zoom="15"
                map-type-id="roadmap"
                style="width: 100%; height: 300px;"
              >
                <GmapMarker
                  :position="medicalCentreLocation"
                  :clickable="true"
                  :draggable="false"
                  :title="site.localMedicalCentre"
                />
              </GmapMap>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row class="section-alert-dark save-btn-container d-flex align-items-center">
        <div class="pr-1">
          <b-button @click="cancelEdit" class="w-100" type="button" variant="secondary">Cancel</b-button>
        </div>
        <div class="pl-1">
          <b-button class="w-100" type="submit" variant="primary">Save</b-button>
        </div>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import { gmapApi } from 'vue2-google-maps';
import { getUsers, updateSite, addSite } from '@/lib/api';
import {
  genericHazards,
  workSafeTasks,
  ppe,
  HAZARD_TYPE_CRITICAL,
  HAZARD_TYPE_STANDARD,
  HAZARD_TYPE_OTHER,
  FieldMap,
  getFieldLabel,
  SectionTitles,
} from '@/lib/generic-lists';
import { EventBus } from '@/lib/event-bus';
import VueTypeaheadBootstrap from 'vue-typeahead-bootstrap';

export default {
  name: 'SiteEdit',
  components: {
    VueTypeaheadBootstrap,
  },
  props: {
    boundSite: {
      type: Object,
      default: () => null,
    },
    editing: {
      type: Boolean,
      default: () => true,
    },
  },
  watch: {
    boundSite() {
      this.loadSite();
    },
  },
  data() {
    return {
      site: {},
      signIns: [],
      rawUsers: [],
      applicableWorkSafeTasks: [],
      applicableCriticalRisks: [],
      applicableStandardRisks: [],
      applicablePPE: [],
      applicableExtraRisks: [],
      firstAiders: [],
      emergencyWardens: [],
      siteLocation: {
        lat: -37.7545637,
        lng: 175.2348051,
      },
      medicalCentreLocation: null,
      FieldMap,
      SectionTitles,
      minChars: 3,
      foreperson: { displayName: '' },
      owner: { displayName: '' },
      submitting: false,
    };
  },
  computed: {
    users() {
      return this.rawUsers.map((user) => ({
        text: user.displayName,
        value: user.mail || user.userPrincipalName,
      }));
    },
    google: gmapApi,
  },
  async mounted() {
    await this.loadSite();
  },
  methods: {
    updateLocation(event) {
      this.siteLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };
    },
    selectOwner(owner) {
      this.owner = owner;
      this.site.ownerUsername = owner.mail;
    },
    selectForeperson(foreperson) {
      this.foreperson = foreperson;
      this.site.forepersonUsername = foreperson.mail;
    },
    setMedicalCentre(place) {
      this.site.localMedicalCentre = place.name;
      this.medicalCentreLocation = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };
    },
    latLngToObject(latLng) {
      if (!latLng || latLng.trim().length === 0) {
        return {
          lat: -37.7545637,
          lng: 175.2348051,
        };
      }
      const parts = latLng.split(/,{1}\s{0,1}/g);
      return {
        lat: parseFloat(parts[0]),
        lng: parseFloat(parts[1]),
      };
    },
    async createSite() {
      // for brand new sites
      this.site = {};
      this.applicableWorkSafeTasks = workSafeTasks.map((workSafeTask) => ({
        description: workSafeTask,
        applies: false,
      }));

      // risks
      this.applicableCriticalRisks = Object.values(genericHazards)
        .filter((genericHazard) => genericHazard.type === HAZARD_TYPE_CRITICAL)
        .map((criticalHazard) => ({
          applies: false,
          risk: criticalHazard.risk,
          type: criticalHazard.type,
        }));

      // then standard
      this.applicableStandardRisks = Object.values(genericHazards)
        .filter((genericHazard) => genericHazard.type === HAZARD_TYPE_STANDARD)
        .map((standardHazard) => ({
          applies: false,
          risk: standardHazard.risk,
          type: standardHazard.type,
        }));
      // now extra
      this.applicableExtraRisks = [];
      // ppe
      this.applicablePPE = ppe.map((ppeItem) => ({
        ...ppeItem,
        applies: ppeItem.name === 'Hi-Viz' || ppeItem.name === 'Steel capped boots' || false,
      }));
      await this.loadUsers();
    },
    async loadSite() {
      // brand new site
      if (!this.boundSite) {
        this.createSite();
        return;
      }

      // first of all make a COPY of the site
      this.site = JSON.parse(JSON.stringify(this.boundSite));
      this.siteLocation = this.latLngToObject(this.site.latLng);
      this.medicalCentreLocation = this.latLngToObject(this.site.medicalCentreLatLng);

      // worksafe notifiable tasks
      this.applicableWorkSafeTasks = (workSafeTasks || []).map((workSafeTask) => ({
        description: workSafeTask,
        applies: this.site.tasks.indexOf(workSafeTask) !== -1,
      }));

      // hazards/risk.
      const keyedSiteHazards = (this.site.hazards || []).map((hazard) => hazard.risk);

      // first critical
      this.applicableCriticalRisks = Object.values(genericHazards || {})
        .filter((genericHazard) => genericHazard.type === HAZARD_TYPE_CRITICAL)
        .map((criticalHazard) => ({
          applies: keyedSiteHazards.indexOf(criticalHazard.risk) !== -1,
          risk: criticalHazard.risk,
          type: criticalHazard.type,
        }));

      // then standard
      this.applicableStandardRisks = Object.values(genericHazards || {})
        .filter((genericHazard) => genericHazard.type === HAZARD_TYPE_STANDARD)
        .map((standardHazard) => ({
          applies: keyedSiteHazards.indexOf(standardHazard.risk) !== -1,
          risk: standardHazard.risk,
          type: standardHazard.type,
        }));

      // then extras
      const genericHazardKeys = Object.values(genericHazards || {}).map((genericHazard) => genericHazard.risk);
      this.applicableExtraRisks = (this.site.hazards || [])
        .filter((hazard) => genericHazardKeys.indexOf(hazard.risk) === -1 && hazard.risk)
        .map((hazard) => ({
          applies: true,
          risk: hazard.risk.replace(/<b>|<\/b>/g, ''),
          type: hazard.type,
          mitigations: hazard.mitigations,
        }));

      // ppe
      this.applicablePPE = ppe.map((ppeItem) => ({
        ...ppeItem,
        applies: this.site.ppe.indexOf(ppeItem.name) !== -1 || ppeItem.name === 'Hi-Viz' || ppeItem.name === 'Steel capped boots',
      }));

      // first aiders and emergency wardens
      this.firstAiders = (this.site.firstAiders || '').split('\n').map((line) => ({
        name: line.split(/\s&#40;/)[0],
        number: (line.split(/\s&#40;/)[1] || '').replace(/(&#40;)|\)/g, ''),
      }));
      this.emergencyWardens = (this.site.emergencyWardens || '').split('\n').map((line) => ({
        name: line.split(/\s&#40;/)[0],
        number: (line.split(/\s&#40;/)[1] || '').replace(/(&#40;)|\)/g, ''),
      }));

      // get the users
      await this.loadUsers();
    },
    async loadUsers() {
      const rawUsers = await getUsers();
      rawUsers.sort((a, b) => (b.displayName > a.displayName ? -1 : 1));
      this.rawUsers = rawUsers.map((o) => ({ text: o.displayName, ...o }));
      if (this.site.ownerUsername) {
        this.owner = this.rawUsers.find((o) => o.mail === this.site.ownerUsername) || this.owner;
      }
      if (this.site.forepersonUsername) {
        this.foreperson = this.rawUsers.find((o) => o.mail === this.site.forepersonUsername) || this.foreperson;
      }
    },
    async saveSite(event) {
      // first of all stop page reload
      event.preventDefault();

      // rudimentary validation
      const errors = [];

      if (!this.site.forepersonUsername) {
        errors.push('A SITE CONTACT is required');
      }

      if (!this.site.ownerUsername) {
        errors.push('A CONTRACT MANAGER is required');
      }

      if (!this.siteLocation || !this.siteLocation.lat) {
        errors.push('A SITE LOCATION could not be found.');
      } else {
        // location
        this.site.latLng = `${this.siteLocation.lat}, ${this.siteLocation.lng}`;
      }

      if (this.medicalCentreLocation) {
        this.site.medicalCentreLatLng = `${this.medicalCentreLocation.lat}, ${this.medicalCentreLocation.lng}`;
      }

      // fill out PPE, hazards and tasks
      this.site.tasks = this.applicableWorkSafeTasks.filter((task) => task.applies).map((task) => task.description);
      this.site.hazards = this.applicableExtraRisks.map((applicableExtraRisk) => {
        applicableExtraRisk.type = HAZARD_TYPE_OTHER; // eslint-disable-line no-param-reassign
        return applicableExtraRisk;
      });

      // get ready to add some risks
      const genericHazardKeys = Object.keys(genericHazards).reduce((accumulator, hazard) => {
        accumulator[genericHazards[hazard].risk] = hazard; // eslint-disable-line no-param-reassign
        return accumulator;
      }, {});

      // add the critical risks
      this.applicableCriticalRisks.filter((risk) => risk.applies).forEach((risk) => {
        this.site.hazards.push({
          risk: genericHazardKeys[risk.risk],
          mitigations: '',
          type: HAZARD_TYPE_CRITICAL,
        });
      });

      // add the standard risks
      this.applicableStandardRisks.filter((risk) => risk.applies).forEach((risk) => {
        this.site.hazards.push({
          risk: genericHazardKeys[risk.risk],
          mitigations: '',
          type: HAZARD_TYPE_STANDARD,
        });
      });

      // ppe
      this.site.ppe = this.applicablePPE
        .filter((ppeItem) => ppeItem.applies)
        .map((ppeItem) => ppeItem.name);

      // first aiders and emergency wardens
      this.site.firstAiders = this.firstAiders
        .filter((firstAider) => firstAider.name && firstAider.name.trim() && firstAider.number && firstAider.number.trim())
        .map((firstAider) => `${firstAider.name} &#40;${firstAider.number})`).join('\n');

      this.site.emergencyWardens = this.emergencyWardens
        .filter((emergencyWarden) => emergencyWarden.name && emergencyWarden.name.trim() && emergencyWarden.number && emergencyWarden.number.trim())
        .map((emergencyWarden) => `${emergencyWarden.name} &#40;${emergencyWarden.number})`).join('\n');

      if (!this.site.firstAiders || this.site.firstAiders.length === 0) {
        errors.push('You must add at least 1 First Aider');
      }

      if (!this.site.emergencyWardens || this.site.emergencyWardens.length === 0) {
        errors.push('You must add at least 1 Emergency Warden');
      }

      if (errors.length > 0) {
        EventBus.$emit('triggerAlert', {
          type: 'danger',
          message: 'Could not save site due to the following errors:',
          items: errors,
        });
        return;
      }

      EventBus.$emit('showLoader');

      // delete a few display fields
      if (this.site.id) delete this.site.name;
      delete this.site.ownerName;
      delete this.site.forepersonName;
      delete this.site.active;
      delete this.site.activeDisplay;
      delete this.site.longid;
      delete this.site.foreperson;
      delete this.site.contractManager;
      delete this.site.criticalRisks;
      delete this.site.additionalRisks;
      delete this.site.standardRisks;
      delete this.site.safety;

      // ok now we're ready to actually save
      try {
        let message;
        let response;
        if (this.site.id) {
          response = await updateSite(this.site);
          message = 'Site updated';
        } else {
          response = await addSite(this.site);
          message = 'Site added';
        }

        if (response && response.status === 200) {
          // site was successfully saved
          EventBus.$emit('triggerAlert', {
            type: 'success',
            message,
          });
          this.$emit('update:site', response.data);
          this.$emit('update:editing', false);
          this.$emit('siteUpdated');
        } else if (response.status && response.status === 422) {
          // validation error
          const validationErrors = response.data.details.map((detail) => {
            const label = getFieldLabel(detail.path[0]);
            return detail.message.replace(detail.path[0], label);
          });

          EventBus.$emit('triggerAlert', {
            type: 'danger',
            message: 'Could not save site',
            items: validationErrors,
          });
        } else {
          // an unhandled response
          throw new Error();
        }
      } catch (e) {
        EventBus.$emit('triggerAlert', {
          type: 'danger',
          message: 'Could not save site',
        });
      }
      EventBus.$emit('hideLoader');
    },
    cancelEdit() {
      this.$emit('cancel');
    },
  },
};
</script>
